/**
 * @fileoverview SupportPages.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import TopImageBanner from '../components/tours/TopImageBanner'
import SEO from '../components/ui/SEO'
import { privacyPolicyText, termsOfUseText, cookiePolicyText } from './PolicyContent'

function FAQItem({ question, answer }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className='border-b border-gray-100 last:border-0'>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className='w-full py-6 flex justify-between items-center gap-4 text-left group'
            >
                <span className={`text-lg font-bold transition-colors ${isOpen ? 'text-KITH-primary' : 'text-gray-800 group-hover:text-KITH-primary'}`}>
                    {question}
                </span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${isOpen ? 'bg-KITH-primary text-white rotate-180' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100'}`}>
                    <i className='fi fi-br-angle-small-down mt-1'></i>
                </div>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                <p className='text-gray-500 leading-relaxed text-justify'>
                    {answer}
                </p>
            </div>
        </div>
    );
}

function PolicyPage({ title, children }) {
  return (
    <div className='flex flex-col w-full min-h-screen pt-32 pb-20 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden'>
      <SEO title={title} />
      {/* Decorative background blur elements */}
      <div className='absolute top-20 left-10 w-96 h-96 bg-KITH-primary/10 rounded-full blur-[100px] pointer-events-none'></div>
      <div className='absolute bottom-20 right-10 w-96 h-96 bg-blue-400/10 rounded-full blur-[100px] pointer-events-none'></div>
      
      <div className='px-4 md:w-[80%] mx-auto w-[90%] relative z-10'>
        <div className='bg-white/0 backdrop-blur-2xl rounded-[3rem] sm:p-2 md:p-4'>
            <h1 className='text-4xl md:text-5xl font-black text-gray-800 mb-10 text-center'>{title}</h1>
            {children}
        </div>
      </div>
    </div>
  )
}

function PlaceholderPage({ title, schema, children }) {
  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20'>
      <SEO title={title} schema={schema} />
      <div className='px-[5%] md:px-[5%]'>
        <div className='relative w-full h-[30vh] md:h-[40vh]'>
          <TopImageBanner title={title} />
        </div>
      </div>
      <div className='px-[5%] md:px-[5%] mt-20 text-left max-w-4xl mx-auto'>
        {children || (
            <>
                <h2 className='text-3xl font-bold text-gray-800 mb-6'>{title}</h2>
                <div className='flex flex-col gap-6 text-gray-600 leading-relaxed text-justify'>
                    <p>
                        Welcome to the {title} page of KITH Travels. We are currently updating our documentation to ensure you have the most accurate and up-to-date information.
                    </p>
                    <div className='w-full h-40 bg-gray-50 rounded-3xl border border-gray-100 flex items-center justify-center italic text-gray-400'>
                        Full content for {title} will be available soon.
                    </div>
                </div>
            </>
        )}
      </div>
    </div>
  )
}

export const FAQ = () => {
    const faqData = [
        { q: "How do I book a customized tour?", a: "You can book a customized tour by contacting us through our 'Start Planning' form or by emailing hello@kithtravels.com. Our experts will work with you to design an itinerary that fits your preferences." },
        { q: "What is included in the tour price?", a: "Typically, our tour prices include accommodation, breakfast, transportation between destinations, and entrance fees for specified attractions. International flights and personal expenses are usually excluded." },
        { q: "Is travel insurance mandatory?", a: "While not mandatory by Sri Lankan law, we strongly recommend all our travelers to have comprehensive travel insurance covering health, accidents, and trip cancellations." },
        { q: "What is your cancellation policy?", a: "Cancellations made 30 days before the tour date are eligible for a 75% refund. Cancellations between 14-30 days receive a 50% refund. Please refer to our full Terms & Conditions for more details." },
        { q: "Do you provide vegetarian or special meal options?", a: "Yes, we cater to all dietary requirements including vegetarian, vegan, gluten-free, and Halal. Please inform us of your dietary needs at the time of booking." }
    ];

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
            }
        }))
    };

    return (
        <PlaceholderPage title="Frequently Asked Questions" schema={faqSchema}>
            <h2 className='text-3xl md:text-5xl font-black text-gray-800 mb-12 text-center'>Common <span className='text-KITH-primary'>Questions</span></h2>
            <div className='flex flex-col bg-white rounded-[2rem] p-8 md:p-12 shadow-xl border border-gray-50'>
                {faqData.map((faq, idx) => (
                    <FAQItem key={idx} question={faq.q} answer={faq.a} />
                ))}
            </div>
        </PlaceholderPage>
    );
};

export const Privacy = () => {
  const { t } = useTranslation();
  return (
    <PolicyPage title={t('nav.privacy_policy')}>
        <div className="prose prose-gray max-w-none text-justify prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-800 prose-h2:mb-4 prose-h2:mt-8 prose-h3:text-xl prose-h3:font-bold prose-h3:text-gray-800 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-justify prose-p:mb-4 prose-ul:list-disc prose-ul:pl-5 prose-ul:text-gray-600 prose-ul:mb-4 prose-li:mb-2 prose-li:text-justify prose-strong:text-KITH-primary prose-strong:font-bold">
            <ReactMarkdown>{t('policy.privacy')}</ReactMarkdown>
        </div>
    </PolicyPage>
  )
};

export const Terms = () => {
  const { t } = useTranslation();
  return (
    <PolicyPage title={t('nav.terms_and_conditions')}>
        <div className="prose prose-gray max-w-none text-justify prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-800 prose-h2:mb-4 prose-h2:mt-8 prose-h3:text-xl prose-h3:font-bold prose-h3:text-gray-800 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-justify prose-p:mb-4 prose-ul:list-disc prose-ul:pl-5 prose-ul:text-gray-600 prose-ul:mb-4 prose-li:mb-2 prose-li:text-justify prose-strong:text-KITH-primary prose-strong:font-bold">
            <ReactMarkdown>{t('policy.terms')}</ReactMarkdown>
        </div>
    </PolicyPage>
  )
};

export const CookiePolicy = () => {
  const { t } = useTranslation();
  return (
    <PolicyPage title={t('nav.cookie_policy')}>
        <div className="prose prose-gray max-w-none text-justify prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-800 prose-h2:mb-4 prose-h2:mt-8 prose-h3:text-xl prose-h3:font-bold prose-h3:text-gray-800 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:text-justify prose-p:mb-4 prose-ul:list-disc prose-ul:pl-5 prose-ul:text-gray-600 prose-ul:mb-4 prose-li:mb-2 prose-li:text-justify prose-strong:text-KITH-primary prose-strong:font-bold">
            <ReactMarkdown>{t('policy.cookie')}</ReactMarkdown>
        </div>
    </PolicyPage>
  )
};
export const Careers = () => <PlaceholderPage title="Careers" />

export const Sitemap = () => {
  const { t } = useTranslation();
  return (
    <PolicyPage title={t('nav.sitemap')}>
        <div className="prose prose-gray max-w-none text-left prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-800 prose-h2:mb-4 prose-h2:mt-8 prose-ul:list-disc prose-ul:pl-5 prose-ul:text-gray-600 prose-ul:mb-4 prose-li:mb-2 prose-a:text-KITH-primary prose-a:no-underline hover:prose-a:underline">
            <ul>
                <li><Link to="/">{t('nav.home')}</Link></li>
                <li>
                    <Link to="/tour-plans">{t('nav.tour_plans')}</Link>
                    <ul>
                        <li><Link to="/custom-tour">{t('tours.start_planning')}</Link></li>
                        <li><Link to="/find-my-tour">{t('nav.my_tour')}</Link></li>
                    </ul>
                </li>
                <li><Link to="/experience">{t('nav.experience')}</Link></li>
                <li>
                    <Link to="/about-sri-lanka">{t('nav.about_sri_lanka') || 'About Sri Lanka'}</Link>
                    <ul>
                        <li><Link to="/when-to-visit">{t('footer.links.when_to_travel')}</Link></li>
                    </ul>
                </li>
                <li><Link to="/hotels">{t('footer.links.hotels')}</Link></li>
                <li><Link to="/blog">{t('footer.links.blog')}</Link></li>
                <li>
                    {t('footer.company')}
                    <ul>
                        <li><Link to="/about-us">{t('nav.about_us')}</Link></li>
                        <li><Link to="/contact-us">{t('nav.contact_us')}</Link></li>
                        <li><Link to="/careers">{t('nav.careers')}</Link></li>
                    </ul>
                </li>
                <li>
                    {t('footer.support')}
                    <ul>
                        <li><Link to="/faq">{t('nav.faq')}</Link></li>
                        <li><Link to="/privacy-policy">{t('nav.privacy_policy')}</Link></li>
                        <li><Link to="/terms-and-conditions">{t('nav.terms_and_conditions')}</Link></li>
                        <li><Link to="/cookie-policy">{t('nav.cookie_policy')}</Link></li>
                    </ul>
                </li>
            </ul>
        </div>
    </PolicyPage>
  )
};
